<template>
    <div id="sales" :class="[$mq]">
        <div id="sidebar">
            <Sidebar 
                :date="date" 
                :summary="summary" 
                @selectDate="selectDate" 
                :bShowTimeframes="$mq !== 'portrait'" 
            />
        </div>

        <div id="board">
            <div class="main-content">
                <Init v-if="!hasValidSalesData" :date="date" />
                <template v-else>
                    <header class="header-topbar">
                        <div class="title">{{ formatDate(date) }}</div>
                        <div class="actions">
                            <button class="button refresh" @click="refresh()" />
                        </div>
                    </header>
                    <div v-if="$mq === 'portrait'" class="timeframes-container">
                        <Timeframes :summary="summary" />
                    </div>
                    <Chart v-if="hasValidSalesData" :title="$t('sales.chart.title')" :data="sales" />
                    <TableView v-if="hasValidSalesData" :title="$t('sales.table.title')" :data="sales" />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import Sidebar from '../components/domain/sales/sidebar.vue'
import Init from '../components/domain/sales/init.vue'
import TableView from '../components/domain/sales/tableView.vue'
import Chart from '../components/domain/sales/chart.vue'
import Timeframes from '@/components/domain/sales/timeframes.vue'
import salesMixin from '@/components/domain/sales/salesMixin.js'

// Constants
const MIN_REFRESH_INTERVAL_MINUTES = 1
const DATE_FORMAT = 'YYYY-MM-DD'

export default {
    name: 'Sales',
    mixins: [salesMixin],
    components: {
        Sidebar,
        Init,
        TableView,
        Chart,
        Timeframes
    },
    data() {
        return {
            date: moment().format(DATE_FORMAT),
            lastRefresh: null
        }
    },
    methods: {
        async load() {
            try {
                this.$overlay.loading()
                this.lastRefresh = moment().toDate()
                await this.$store.dispatch('sales/loadData', this.date)
            } catch (error) {
                logError('Error loading sales data:', error)
            } finally {
                this.$overlay.hide()
            }
        },
        selectDate(date) {
            const formattedDate = moment(date).format(DATE_FORMAT)
            if (this.date !== formattedDate) {
                this.date = formattedDate
                this.load()
            }
        },
        refresh() {
            if (this.isRefreshThrottled()) {
                return
            }
            this.load()
        },
        isRefreshThrottled() {
            if (!this.lastRefresh) {
                return false
            }
            const oneMinuteAgo = moment().subtract(MIN_REFRESH_INTERVAL_MINUTES, 'minute').toDate()
            return this.lastRefresh > oneMinuteAgo
        }
    },
    created() {
        this.load()
    }
}
</script>

<style lang="scss" scoped>
#sales {
    display: flex;
    width: 100%;

    #sidebar {
        width: 300px;
        flex: 0 0 300px;
        height: 100%;
        background-color: #fff;
        position: absolute;
    }

    #board {
        padding: 16px;
        border-radius: 4px;
        width: calc(100% - 300px);
        flex: 0 0 calc(100% - 300px);
        position: absolute;
        left: 300px;
        height: 100%;
        min-height: 0;
        min-width: 0;
        overflow-y: auto;

        &.collapsed {
            width: calc(100% - 60px);
            left: 60px;
        }

        header {
            &.header-container {
                margin-bottom: 8px;
            }
        }

        .main-content {
            .header-topbar {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
                
                .title {
                    font-family: $text-bold;
                    @include font-size(l);
                }

                .actions {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;

                    .button {
                        width: 40px;
                        height: 40px;
                        border-radius: 4px;

                        &.refresh {
                            background-color: $color-white;
                            @include background($image: img('recargar.svg'), $position: center, $size: 20px);
                        }
                    }
                }
            }
        }
    }

    &.portrait {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;

        #sidebar {
            width: 100%;
            position: relative;
            height: auto;
            flex: 100%;
            background-color: transparent;
        }

        #board {
            width: 100% !important;
            position: relative;
            left: 0;
            padding: 8px;
            overflow-x: hidden;

            .main-content {
                .header-topbar {
                    .title {
                        @include font-size(s);
                    }
                }

                .timeframes-container {
                    margin: 8px 0;
                }
            }
        }
    }
}
</style>
